import React, { useState, useEffect, useContext } from "react"
import { compose } from "recompose"
import PropTypes from "prop-types"
import moment from "moment"
import {
  Box,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  ChakraProvider,
  Stack,
  Text,
  HStack,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormErrorMessage,
} from "@chakra-ui/react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useToasts } from "react-toast-notifications"
import { useTranslation } from "react-i18next"
import { withFirebase } from "../../utils/Firebase"
import Nav from "../../components/atoms/Nav"
import { withAuthorization, AuthUserContext } from "../../context/Session"
import { generatePassword, deleteUserRequest } from "../../utils/App"
import * as ROLES from "../../utils/Constants/Roles"

const createAdminUrl = `${process.env.REACT_APP_FUNCTIONS_URL}/authUserCreateNew`
const mailerUrl = `${process.env.REACT_APP_FUNCTIONS_URL}/mailerSend`

const Communicators = ({ firebase }) => {
  const authUser = useContext(AuthUserContext)
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()

  const [isCreating, setIsCreating] = useState(false)
  const [communicators, setCommunicators] = useState([])
  const [currentUser, setCurrentUser] = useState(null)

  const getCommunicators = async () => {
    const communicatorsFromApi = await firebase.getOrgCommunicators(
      authUser?.organizationId
    )
    const docs = communicatorsFromApi.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))
    setCommunicators(docs)
  }

  useEffect(() => {
    if (firebase && authUser?.organizationId) {
      getCommunicators()
    }
  }, [firebase, authUser?.organizationId])

  const successHandler = () => {
    getCommunicators()
    addToast(t("saved_successfully"), {
      appearance: "success",
      placement: "top-right",
    })
    setIsCreating(false)
    onClose()
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      surname1: "",
      surname2: "",
      email: "",
      password: generatePassword(),
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Nombre requerido"),
      surname1: Yup.string().required("Primer Apellido requerido"),
      surname2: Yup.string(),
      email: Yup.string().email("Email inválido").required("Email requerido"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsCreating(true)
      try {
        const userValues = {
          ...values,
          displayName: `${values?.name} ${values?.surname1} ${values?.surname2}`,
        }

        if (currentUser) {
          const userUpdatePayload = {
            displayName: userValues?.displayName,
            name: values.name,
            surname1: values.surname1,
            surname2: values.surname2,
          }

          await firebase.saveUserData({
            email: values.email,
            data: userUpdatePayload,
          })

          await firebase.editOrgCommunicator({
            orgId: authUser?.organizationId,
            communicatorId: currentUser.id,
            updatedData: userUpdatePayload,
          })

          successHandler()
        } else {
          const token = await firebase.getIdToken()
          fetch(createAdminUrl, {
            method: "POST",
            body: JSON.stringify({ user: userValues }),
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }).then(async (dataResponse) => {
            const parsedRes = await dataResponse.json()
            const { error } = parsedRes

            if (
              error &&
              error?.message?.includes("The email address is already in use")
            ) {
              addToast(
                "La dirección de correo ya está siendo utilizada por otra cuenta.",
                {
                  appearance: "error",
                  placement: "top-right",
                }
              )
              return
            }

            if (dataResponse.status === 200) {
              let orgData = null
              if (authUser?.organizationId) {
                const currentOrg = await firebase
                  .org(authUser?.organizationId)
                  .get()
                orgData = currentOrg.data()
              }

              await firebase.addOrgCommunicators({
                orgId: authUser?.organizationId,
                email: values.email,
                communicatorData: {
                  ...values,
                  displayName: userValues?.displayName,
                  active: true,
                  createdAt: Date.now(),
                  role: "communicator",
                },
              })

              const userUpdatePayload = {
                displayName: userValues?.displayName,
                name: values.name,
                surname1: values.surname1,
                surname2: values.surname2,
                email: values.email,
                active: true,
                status: "active",
                country: orgData?.country,
                userType: "admin",
                organizationId: authUser?.organizationId,
                roles: {
                  COMMUNICATOR: true,
                },
              }

              await firebase.saveUserData({
                email: values.email,
                data: userUpdatePayload,
              })

              await fetch(mailerUrl, {
                method: "POST",
                body: JSON.stringify({
                  data: {
                    templateId: 7056954, // new communicator created
                    email: values.email,
                    variables: {
                      displayName: userValues.displayName,
                      password: values.password,
                      orgName: orgData?.businessName,
                      websiteLink: process.env.REACT_APP_ADMIN_APP_SIGNIN,
                    },
                  },
                }),
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              })

              successHandler()
            }
          })
        }

        resetForm({ value: "" })
      } catch (e) {
        addToast(t("unexpected_error"), {
          appearance: "error",
          placement: "top-right",
        })
        onClose()
        setIsCreating(false)
      }
    },
  })

  const handleOpenCreate = () => {
    setCurrentUser(null)
    formik.resetForm()
    onOpen()
  }

  const handleEdit = (user) => {
    setCurrentUser(user)
    formik.setValues({
      name: user.name,
      surname1: user.surname1,
      surname2: user.surname2,
      email: user.email,
    })
    onOpen()
  }

  const handleDelete = (user) => {
    setCurrentUser(user)
    onDeleteOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!currentUser?.id) return

    try {
      setIsCreating(true)
      const token = await firebase.getIdToken()

      await fetch(deleteUserRequest, {
        method: "POST",
        body: JSON.stringify({ userToDeleteEmail: currentUser.email }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      await firebase.removeUserByEmail({ email: currentUser.email })

      await firebase.removeOrgCommunicator({
        orgId: authUser?.organizationId,
        communicatorId: currentUser.id,
      })

      setCommunicators((prev) => prev.filter((c) => c.id !== currentUser.id))

      addToast(t("saved_successfully"), {
        appearance: "success",
        placement: "top-right",
      })

      setTimeout(() => {
        onDeleteClose()
      }, 100)
    } catch (error) {
      addToast(t("unexpected_error"), {
        appearance: "error",
        placement: "top-right",
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <ChakraProvider>
      <Nav
        titleKey="communicators_tite"
        descriptionKey="communicators_description"
      />

      <Box display="flex" height="100vh" flexDirection="column">
        <Box
          mt={10}
          pr={10}
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="flex-end"
          alignItems="flex-end"
        >
          <Button
            colorScheme="blue"
            onClick={handleOpenCreate}
            isLoading={isCreating}
          >
            Crear nuevo
          </Button>
        </Box>

        <Box width="60%" minWidth="250px" p={4}>
          <Text fontSize="xl" mb={4} fontWeight="bold">
            Comunicadores
          </Text>
          <Stack spacing={4}>
            {communicators?.length > 0 ? (
              communicators.map((communicator) => (
                <Box
                  key={communicator.id}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  display="flex"
                  flexDirection="column"
                >
                  <HStack justifyContent="space-between">
                    <Box>
                      <Text fontWeight="bold">
                        {communicator?.name} {communicator?.surname1}{" "}
                        {communicator?.surname2}
                      </Text>
                      <Text fontSize="sm">{communicator.email}</Text>
                      <Text fontSize="xs" color="gray.500">
                        Creado:{" "}
                        {moment(communicator.createdAt).format("MMM DD, YYYY")}
                      </Text>
                    </Box>
                    <Spacer />
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => handleEdit(communicator)}
                        disabled={isCreating}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDelete(communicator)}
                        disabled={isCreating}
                      >
                        Eliminar
                      </Button>
                    </HStack>
                  </HStack>
                </Box>
              ))
            ) : (
              <Box p={3} display="flex" flexDirection="row">
                <Text fontWeight="bold">- Sin Comunicadores creados,</Text>
                <Text
                  textDecoration="underline"
                  onClick={handleOpenCreate}
                  cursor="pointer"
                  fontWeight="bold"
                  ml="5px"
                  color="#4d66f5"
                >
                  desea crear un nuevo comunicador ?
                </Text>
              </Box>
            )}
          </Stack>

          <Drawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            size="md"
            closeOnOverlayClick={!isCreating}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader borderBottomWidth="1px">
                {currentUser ? "Editar Comunicador" : "Crear Nuevo Comunicador"}
              </DrawerHeader>
              <DrawerBody>
                <form onSubmit={formik.handleSubmit}>
                  <FormControl
                    mb={3}
                    isInvalid={formik.touched.name && formik.errors.name}
                  >
                    <FormLabel>Nombre</FormLabel>
                    <Input
                      placeholder="Nombre"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    mb={3}
                    isInvalid={
                      formik.touched.surname1 && formik.errors.surname1
                    }
                  >
                    <FormLabel>Primer Apellido</FormLabel>
                    <Input
                      placeholder="Primer Apellido"
                      name="surname1"
                      value={formik.values.surname1}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>
                      {formik.errors.surname1}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl mb={3}>
                    <FormLabel>Segundo Apellido</FormLabel>
                    <Input
                      placeholder="Segundo Apellido"
                      name="surname2"
                      value={formik.values.surname2}
                      onChange={formik.handleChange}
                    />
                  </FormControl>

                  <FormControl
                    isInvalid={formik.touched.email && formik.errors.email}
                  >
                    <FormLabel>Correo electrónico</FormLabel>
                    <Input
                      placeholder="Correo electrónico"
                      type="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={currentUser}
                    />
                    <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                  </FormControl>
                </form>
              </DrawerBody>
              <DrawerFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={formik.handleSubmit}
                  type="submit"
                  isLoading={isCreating}
                >
                  Guardar
                </Button>
                <Button onClick={onClose} disabled={isCreating}>
                  Cancelar
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <Modal
            isOpen={isDeleteOpen}
            onClose={onDeleteClose}
            isCentered
            closeOnOverlayClick={!isCreating}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Confirmar eliminación</ModalHeader>
              <ModalBody>
                ¿Estás seguro que deseas eliminar a {currentUser?.name}{" "}
                {currentUser?.surname1}?
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="red"
                  mr={3}
                  onClick={handleDeleteConfirm}
                  isLoading={isCreating}
                >
                  Eliminar
                </Button>
                <Button onClick={onDeleteClose} disabled={isCreating}>
                  Cancelar
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Box>
    </ChakraProvider>
  )
}

Communicators.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) =>
  ((!!authUser && authUser.userType === "admin") ||
    (!!authUser && authUser.userType === "business_partner")) &&
  authUser.hasMessagingAccess &&
  (authUser.roles[ROLES.BUSINESS_ADMIN] || authUser.roles[ROLES.COMMUNICATOR])

const ComposedCommunicators = compose(
  withFirebase,
  withAuthorization(condition)
)(Communicators)
export default ComposedCommunicators
